import React, { useState, useRef, useEffect } from 'react';
import {cloneDeep} from "lodash";
import InvisiblePane from "../../Components/InvisiblePane";
import Text from "../../Components/Text";
import ProgressBar from "../../Components/ProgressBar";
import Pane from "../../Components/Pane";
import BackButton from "../../Components/BackButton";
import Button from "../../Components/Button";
import PrimaryTitle from "../../Components/PrimaryTitle";
import {DATE_FORMATS, DEFAULT_ERROR_MESSAGE, TITLE_OPTIONS} from "../../constants";
import Error from "../../Components/Error";
import PageLoadingPanel from "../../Components/PageLoadingPanel";
import Spacer from "../../Components/Spacer";
import ClientDetails from "./Components/ClientDetails";
import SecondaryTitle from "../../Components/SecondaryTitle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import {format, parse, startOfDay} from "date-fns";

const DEFAULT_CLIENT = {
    'title':                                         '',
    'first_name':                                    '',
    'last_name':                                     '',
    'email':                                         '',
    'phone_prefix':                                  '',
    'phone_number':                                  '',
    'dual_nationality':                              0,
    'primary_nationality':                           '',
    'secondary_nationality':                         '',
    'date_of_birth':                                 '',
    'national_insurance_number':                     '',
    'password':                                      '',
    'phone_password':                                '',
    'occupation':                                    '',
    'residential_address_line_1':                    '',
    'residential_address_line_2':                    '',
    'residential_town_city':                         '',
    'residential_state_county':                      '',
    'residential_postcode':                          '',
    'residential_country':                           '',
    'residential_occupied_since_year':               '',
    'residential_occupied_since_month':              '',
    'permanent_address_is_correspondence_address':   0,
    'correspondence_address_line_1':                 '',
    'correspondence_address_line_2':                 '',
    'correspondence_town_city':                      '',
    'correspondence_state_county':                   '',
    'correspondence_postcode':                       '',
    'correspondence_country':                        '',
    'correspondence_occupied_since_year':            '',
    'correspondence_occupied_since_month':           '',
    'uuid':                                          '',
    'primary_client':                                0,
};

const ClientDetailsPage = (props) => {
    const {
        previousPage    = () => {},
        nextPage        = () => {}
    } = props;

    const PAGE_TITLE            = 'Client Details';
    const CURRENT_STEP_NUMBER   = 2;
    const TOTAL_STEP_NUMBER     = 8;

    const [clients, setClients]                     = useState([cloneDeep(DEFAULT_CLIENT)]);
    const [loaded, setLoaded]                       = useState(false);
    const [errors, setErrors]                       = useState([]);
    const [errorMessage, setErrorMessage]           = useState(null);
    const [submitting, setSubmitting]               = useState(false);

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        if (submitting) {
            submit();
        }
    }, [submitting]);

    const load = () => {
        axios.get(route('web-api.session.client-details.get', {session: window.session}).toString())
            .then((response) => {
                if (!!response && !!response.data) {
                    setClients(response.data.data);
                }
            })
            .catch((error) => {
                console.log(error);
            }).finally(() => {
                setLoaded(true);
            });
    }

    const submit = () => {
        setErrorMessage(null);

        let clone = cloneDeep(clients);
        for (let i = 0; i < clone.length; i++) {
            if (clone[i].date_of_birth !== null && clone[i].date_of_birth !== '') {
                clone[i].date_of_birth = format(parse(clone[i].date_of_birth, DATE_FORMATS.DATE, startOfDay(new Date())), DATE_FORMATS.DB_DATE);
            }
        }

        axios.patch(route('web-api.session.client-details.update', {session: window.session}).toString(), {
            client_details: clone
        })
            .then((response) => {
                setSubmitting(false);
                nextPage();
            })
            .catch((error) => {
                if (!!error && !!error.response && !!error.response.data) {
                    if (!!error.response.data.message) {
                        setErrorMessage(error.response.data.message);
                    }

                    if (!!error.response.data.errors) {
                        // Update errors for each client
                        let errors = Array(clients.length).fill({});
                        for (const errorField in error.response.data.errors) {
                            const [clientDetails, clientIndex, fieldName] = errorField.split('.');
                            if (clientDetails === "client_details") {
                                errors[clientIndex] = {
                                    ...errors[clientIndex],
                                    [fieldName]: error.response.data.errors[errorField][0],
                                };
                            }
                        }
                        setErrors(errors);
                    }

                    setSubmitting(false);
                } else {
                    console.log(error);
                    setErrorMessage(DEFAULT_ERROR_MESSAGE);
                }
            })
            .finally(() => {
                setSubmitting(false); // Ensure that submitting is set to false in all cases
            });
    }

    const updateField = (index, field, value) => {
        let updatedClients = cloneDeep(clients);
        updatedClients[index][field] = value;
        setClients(updatedClients);

        // Clear the error for the updated field
        let updatedErrors = [...errors];
        updatedErrors[index] = {
            ...updatedErrors[index],
            [field]: null,
        };
        setErrors(updatedErrors);
    };

    const addClient = () => {
        if (clients.length >= 2) {
            return;
        }

        const clonedClients = cloneDeep(clients);
        clonedClients.push(cloneDeep(DEFAULT_CLIENT));
        setClients(clonedClients);
    };

    const removeClient = () => {
        if (clients.length <= 1) {
            return;
        }

        const clonedClients = cloneDeep(clients);
        clonedClients.pop();
        setClients(clonedClients);
    };

    const renderTopText = () => {
        return (
            <InvisiblePane style={{paddingTop: 0}}>
                <Text style={{marginBottom: '0', textAlign: 'center', fontWeight: 'bold'}}>Please check the business details - the better we understand your business, the easier it is for us to help you</Text>
            </InvisiblePane>
        );
    }

    if (!loaded) {
        return (
            <PageLoadingPanel
                pageTitle={PAGE_TITLE}
                currentStepNumber={CURRENT_STEP_NUMBER}
                totalStepNumber={TOTAL_STEP_NUMBER}
                upperTextComponent={renderTopText()}
            />
        )
    }

    return (
        <React.Fragment>
            {renderTopText()}
            {!!errorMessage && (
                <Error message={errorMessage} />
            )}
            <Pane>
                <ProgressBar
                    title={PAGE_TITLE}
                    currentStepNumber={CURRENT_STEP_NUMBER}
                    totalStepNumber={TOTAL_STEP_NUMBER}
                />
                {clients.map((client, index) => (
                    <div key={index}>
                        <PrimaryTitle>{PAGE_TITLE}</PrimaryTitle>
                        <ClientDetails
                            index={index}
                            data={client}
                            errors={errors[index]}
                            updateField={(field, value) => updateField(index, field, value)}
                            submitting={submitting}
                        />

                        {/*{index >= 1 ? (*/}
                        {/*        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>*/}
                        {/*            <PrimaryTitle>Client {index+1} Details</PrimaryTitle>*/}
                        {/*            <SecondaryTitle onClick={() => removeClient()} style={{cursor: 'pointer'}}><FontAwesomeIcon icon={faTrash} /></SecondaryTitle>*/}
                        {/*        </div>*/}
                        {/*) : (*/}
                        {/*    <PrimaryTitle>Client Details (You)</PrimaryTitle>*/}
                        {/*)}*/}
                        {/*<ClientDetails*/}
                        {/*    index={index}*/}
                        {/*    data={client}*/}
                        {/*    errors={errors[index]}*/}
                        {/*    updateField={(field, value) => updateField(index, field, value)}*/}
                        {/*    submitting={submitting}*/}
                        {/*/>*/}
                        {/*<Spacer />*/}
                    </div>
                ))}
                {/*{clients.length <= 1 && (*/}
                {/*    <SecondaryTitle*/}
                {/*        style={{ marginLeft: 'auto', cursor: 'pointer', width: 'fit-content' }}*/}
                {/*        onClick={addClient}*/}
                {/*    >*/}
                {/*        Add client/account holder +*/}
                {/*    </SecondaryTitle>*/}
                {/*)}*/}
            </Pane>
            {!!errorMessage && (
                <Error message={errorMessage} />
            )}
            <InvisiblePane style={{paddingTop: '59px', paddingLeft: 0, paddingRight: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <BackButton
                    disabled={submitting}
                    onClick={() => previousPage()}
                />
                <Button
                    label={submitting ? 'Submitting...' : 'Next'}
                    disabled={submitting}
                    onClick={() => setSubmitting(true)}
                />
            </InvisiblePane>
        </React.Fragment>
    );
}

export default ClientDetailsPage;
